import React from "react";
import { Head, Link, router } from "@inertiajs/react";
import { Box, Grid, Group, Stack, Button, Select } from "@mantine/core";
import { IconFilter, IconX } from "@tabler/icons-react";
import { ProjectCard } from "../../Components/ProjectCard";
import { LatestPostsStrip } from "../../Components/LatestPostsStrip";
import PageBackgroundPhoto from "../../Components/Layout/PageBackgroundPhoto";
import {
  Heading1,
  Heading2,
  DimmedText,
  MainText,
} from "../../Components/UI/Typography";
import { Card } from "../../Components/UI/Card";
import PageShell from "../../Components/Layout/PageShell";
import type { ProjectsIndexProps } from "../../src/types/project";

const BuildIndex: React.FC<ProjectsIndexProps> = ({
  projects,
  project_types,
  all_skills,
  current_filters,
}) => {
  const hasActiveFilters =
    current_filters.type || current_filters.skill || current_filters.tool;

  const handleFilter = (key: string, value: string | null) => {
    router.get(
      "/tech",
      { ...current_filters, [key]: value || undefined },
      { preserveState: true, replace: true }
    );
  };

  const clearFilters = () => {
    router.get("/tech", {}, { replace: true });
  };

  return (
    <>
      <Head>
        <title>Build | Ogarocious</title>
        <meta
          name="description"
          content="Tech products, AI consulting, digital media, and writing — the work of Charles Ogar / Ogarocious."
        />
        <meta property="og:title" content="Build | Ogarocious" />
        <meta
          property="og:description"
          content="Tech products, AI consulting, digital media, and writing — the work of Charles Ogar / Ogarocious."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${import.meta.env.VITE_SITE_URL || ""}/tech`}
        />
        <meta
          property="og:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Build | Ogarocious" />
        <meta
          name="twitter:description"
          content="Tech products, AI consulting, digital media, and writing."
        />
        <meta
          name="twitter:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell
        variant="section"
        extraBackgrounds={
          <PageBackgroundPhoto src={import.meta.env.VITE_TECH_PHOTO_URL} />
        }
      >
        <Stack gap={48}>
            {/* ── Page Header ── */}
            <Stack gap="md" align="center">
              <Heading1 variant="shadow">Build</Heading1>
              <DimmedText
                size="lg"
                style={{ textAlign: "center", maxWidth: "clamp(280px, 90vw, 560px)" }}
              >
                Tech products, AI consulting, digital media, and writing — the
                full body of work as a Creative Technologist.
              </DimmedText>
            </Stack>

            {/* ── Bio ── */}
            <Card padding="xl" radius="md">
              <Stack gap="md">
                <MainText style={{ lineHeight: 1.75 }}>
                  I build operational infrastructure — automation systems, data pipelines, and AI integrations that make organizations run without the overhead of a full technical team. My work spans no-code orchestration, full-stack development, and AI implementation, often all three on the same project.
                </MainText>
                <MainText style={{ lineHeight: 1.75 }}>
                  Recent work includes a 26-workflow automation platform connecting 12 external APIs — CRM, communication tools, document signing, AI providers, and more — into a unified system for a nonprofit org. I built the intake pipeline, enrollment tracking, weekly touchpoint automation, daily AI briefing system, and the data sync architecture tying it all together. I also audited 597 client records with accumulated structural corruption, traced the root cause to batch import logic, rewrote the sync layer, and resolved it.
                </MainText>
                <MainText style={{ lineHeight: 1.75 }}>
                  On the product side I'm building AI-native tools that combine multi-agent extraction, document analysis, and domain logic into client-facing applications. I write Rails, React, TypeScript, and Python — and I tend to build systems that scale past what a single developer is expected to ship alone.
                </MainText>
              </Stack>
            </Card>

            {/* ── Filters ── */}
            <Box>
              <Group align="flex-start" gap="md" wrap="wrap">
                <Group gap="xs" align="center">
                  <IconFilter
                    size={16}
                    style={{ color: "rgba(161, 161, 170, 0.6)" }}
                  />
                  <DimmedText size="sm">Filter by:</DimmedText>
                </Group>

                <Select
                  placeholder="All project types"
                  data={project_types}
                  value={current_filters.type || null}
                  onChange={(val) => handleFilter("type", val)}
                  clearable
                  styles={{
                    input: {
                      background: "rgba(26, 27, 30, 0.95)",
                      border: "1px solid rgba(55, 58, 64, 0.4)",
                      color: "white",
                    },
                    dropdown: {
                      background: "rgba(26, 27, 30, 0.98)",
                      border: "1px solid rgba(55, 58, 64, 0.4)",
                    },
                    option: { color: "white" },
                  }}
                  style={{ minWidth: "clamp(140px, 45vw, 200px)" }}
                />

                <Select
                  placeholder="All skills"
                  data={all_skills}
                  value={current_filters.skill || null}
                  onChange={(val) => handleFilter("skill", val)}
                  clearable
                  searchable
                  styles={{
                    input: {
                      background: "rgba(26, 27, 30, 0.95)",
                      border: "1px solid rgba(55, 58, 64, 0.4)",
                      color: "white",
                    },
                    dropdown: {
                      background: "rgba(26, 27, 30, 0.98)",
                      border: "1px solid rgba(55, 58, 64, 0.4)",
                    },
                    option: { color: "white" },
                  }}
                  style={{ minWidth: "clamp(130px, 40vw, 180px)" }}
                />

                {hasActiveFilters && (
                  <Button
                    variant="subtle"
                    color="gray"
                    size="sm"
                    leftSection={<IconX size={14} />}
                    onClick={clearFilters}
                  >
                    Clear filters
                  </Button>
                )}
              </Group>
            </Box>

            {/* ── Projects Grid ── */}
            {projects.length > 0 ? (
              <Grid>
                {projects.map((project) => (
                  <Grid.Col
                    key={project.id}
                    span={{ base: 12, sm: 6, lg: 4 }}
                  >
                    <ProjectCard project={project} />
                  </Grid.Col>
                ))}
              </Grid>
            ) : (
              <Stack align="center" gap="md" py={60}>
                <Heading2>No projects found</Heading2>
                <DimmedText>
                  Try adjusting your filters or{" "}
                  <Button
                    variant="subtle"
                    color="teal"
                    size="sm"
                    p={0}
                    onClick={clearFilters}
                    style={{ display: "inline" }}
                  >
                    clear all filters
                  </Button>
                  .
                </DimmedText>
              </Stack>
            )}

            {/* ── Latest Posts ── */}
            <LatestPostsStrip pillar="tech" />

            {/* ── Back to Home ── */}
            <Group justify="center">
              <Button variant="outline" size="md" component={Link} href="/">
                Back to Home
              </Button>
            </Group>
        </Stack>
      </PageShell>
    </>
  );
};

export default BuildIndex;
